(function () {
  'use strict';

  /**
   * Updates data-option-available for swatch fieldsets based on
   * currently selected other options, using actual variant availability.
   *
   * Problem: Shopify's server-side product_option_value.available returns true
   * as long as the option value is available in ANY variant combination.
   * For color swatches (first option), this means they always appear available
   * even when the current size/other option combination is sold out.
   *
   * This script re-evaluates availability client-side after each variant change.
   */
  class SwatchAvailabilityUpdater {
    /** @type {Array<{available: boolean, options: string[]}>} */
    #variants = [];

    /** @type {HTMLElement} */
    #picker;

    /** @param {HTMLElement} picker */
    constructor(picker) {
      this.#picker = picker;
    }

    async init() {
      const productUrl = this.#picker.dataset.productUrl?.split('?')[0];
      if (!productUrl) return;

      try {
        const res = await fetch(`${productUrl}.js`);
        if (!res.ok) return;
        const product = await res.json();
        this.#variants = product.variants.map((v) => ({
          available: v.available,
          options: [v.option1, v.option2, v.option3],
        }));
      } catch {
        return;
      }

      // Initial update
      this.#update();

      // Re-run after every variant update (fires after morph completes)
      this.#picker.addEventListener('variant:update', () => this.#update());
    }

    #update() {
      if (!this.#variants.length) return;

      const fieldsets = Array.from(
        this.#picker.querySelectorAll('fieldset[data-fieldset-index]')
      );

      fieldsets.forEach((fieldset, fieldsetIndex) => {
        // Only process swatch fieldsets
        if (!fieldset.querySelector('.swatch')) return;

        // Get the currently selected values from OTHER fieldsets
        const otherSelected = fieldsets
          .map((fs, idx) => ({
            index: idx,
            value: fs.querySelector('input[data-current-checked="true"]')?.value ?? null,
          }))
          .filter((s) => s.index !== fieldsetIndex && s.value !== null);

        // For each swatch input, check if ANY available variant matches
        // this swatch value combined with all other currently selected options
        fieldset.querySelectorAll('input[data-option-available]').forEach((input) => {
          const swatchValue = input.value;

          const isAvailable = this.#variants.some((variant) => {
            if (!variant.available) return false;
            if (variant.options[fieldsetIndex] !== swatchValue) return false;
            return otherSelected.every(
              (sel) => variant.options[sel.index] === sel.value
            );
          });

          input.setAttribute('data-option-available', isAvailable ? 'true' : 'false');
        });
      });
    }
  }

  function initAll() {
    document.querySelectorAll('variant-picker[data-product-url]').forEach((picker) => {
      new SwatchAvailabilityUpdater(picker).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
